import { memo, useLayoutEffect, useRef, type KeyboardEvent, type MouseEvent } from 'react'
import type { NodeId } from '@solar/core'
import { handleEditKey } from '../client/keys.ts'
import { useNode, useStore } from './hooks.ts'
import { DueChip, FieldGrid } from './fields.tsx'
import { Content, sourceOffsetAt } from './render.tsx'
import { dayTitle } from './dates.ts'

const fieldSizing = typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content')

function autosize(ta: HTMLTextAreaElement): void {
  ta.style.height = 'auto'
  ta.style.height = `${ta.scrollHeight}px`
}

export const DayHeader = memo(function DayHeader({ date, onOpen }: { date: string; onOpen: () => void }) {
  const t = dayTitle(date)
  return (
    <div className="day">
      <h2>
        <a href={`/d/${date}`} onClick={(e) => (e.preventDefault(), onOpen())}>
          {t.title}
        </a>{' '}
        <span className="weekday">{t.weekday}</span>
      </h2>
    </div>
  )
})

export interface BlockRowProps {
  id: NodeId
  root: NodeId
  depth: number
  header: boolean
  active: boolean
  selected: boolean
  hasKids: boolean
  caretSeq: number
}

export const BlockRow = memo(function BlockRow(p: BlockRowProps) {
  const store = useStore()
  const node = useNode(p.id)
  if (!node) return null
  const folded = node.collapsed && p.hasKids
  const onContentDown = (e: MouseEvent): void => {
    if ((e.target as HTMLElement).closest('a, .embed')) return
    e.preventDefault()
    if (e.shiftKey) store.shiftClick(p.id, p.root)
    else store.edit(p.id, p.root, sourceOffsetAt(e.clientX, e.clientY) ?? 'end')
  }
  return (
    <div className={`row${p.selected ? ' sel' : ''}${p.header ? ' header' : ''}${p.active ? ' active' : ''}`} style={{ paddingLeft: Math.max(0, p.depth) * 24 }} data-id={p.id}>
      {!p.header && (
        <span
          className="arrow"
          onMouseDown={(e) => {
            e.preventDefault()
            if (p.hasKids) store.toggle(p.id, p.root)
          }}
        >
          {p.hasKids ? (node.collapsed ? '▸' : '▾') : ''}
        </span>
      )}
      {!p.header && (
        <span
          className="bullet"
          title="Zoom in"
          onMouseDown={(e) => {
            e.preventDefault()
            store.zoomIn(p.id)
          }}
        >
          <span className={folded ? 'dot folded' : 'dot'} />
        </span>
      )}
      <div className="body">
        {p.active ? (
          <Editor id={p.id} content={node.content} caretSeq={p.caretSeq} />
        ) : (
          <div className="content" onMouseDown={onContentDown}>
            <Content content={node.content} nodeId={node.id} />
            <DueChip node={node} />
          </div>
        )}
        <FieldGrid node={node} active={p.active} />
      </div>
    </div>
  )
})

export const GhostRow = memo(function GhostRow(p: { id: string; root: NodeId; active: boolean; caretSeq: number; label: string }) {
  const store = useStore()
  return (
    <div className="row ghost" data-id={p.id}>
      <span className="arrow" />
      <span className="bullet">
        <span className="dot faint" />
      </span>
      {p.active ? (
        <Editor id={p.id} content="" caretSeq={p.caretSeq} placeholder={p.label} />
      ) : (
        <div
          className="content placeholder-text"
          onMouseDown={(e) => {
            e.preventDefault()
            store.edit(p.id, p.root)
          }}
        >
          {p.label}
        </div>
      )}
    </div>
  )
})

function Editor({ id, content, caretSeq, placeholder }: { id: string; content: string; caretSeq: number; placeholder?: string }) {
  const store = useStore()
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const ta = ref.current!
    store.textarea = ta
    if (!fieldSizing) autosize(ta)
    let shiftEnter = false
    const onKeyDownRaw = (e: globalThis.KeyboardEvent): void => {
      shiftEnter = e.key === 'Enter' && e.shiftKey
    }
    // Android keyboards report keyCode 229 while composing; beforeinput carries the intent (D41).
    const onBeforeInput = (e: InputEvent): void => {
      if ((e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') && !shiftEnter && !e.isComposing) {
        e.preventDefault()
        if (store.picker) store.pickItem()
        else store.split()
      } else if (e.inputType === 'insertText' && !e.isComposing && store.autoPair(ta, e.data)) {
        e.preventDefault()
      } else if (e.inputType === 'deleteContentBackward' && ta.selectionStart === 0 && ta.selectionEnd === 0) {
        e.preventDefault()
        store.merge()
      }
    }
    const onInput = (): void => {
      store.onInput(ta)
      if (!fieldSizing) autosize(ta)
    }
    // The caret moved without typing (arrows, click): the picker follows or closes.
    const onCaretMove = (e: Event): void => {
      if (!store.picker) return
      if (e instanceof KeyboardEvent && !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
      store.updatePicker(ta)
    }
    ta.addEventListener('keydown', onKeyDownRaw, true)
    ta.addEventListener('beforeinput', onBeforeInput)
    ta.addEventListener('input', onInput)
    ta.addEventListener('keyup', onCaretMove)
    ta.addEventListener('mouseup', onCaretMove)
    return () => {
      ta.removeEventListener('keydown', onKeyDownRaw, true)
      ta.removeEventListener('beforeinput', onBeforeInput)
      ta.removeEventListener('input', onInput)
      ta.removeEventListener('keyup', onCaretMove)
      ta.removeEventListener('mouseup', onCaretMove)
      if (store.textarea === ta) {
        store.commitDraft()
        store.textarea = null
      }
    }
  }, [store])

  // Text first, then the caret: a caret request after a change (⌘⏎ adds a prefix) must land in
  // the new text, and React runs layout effects in order.
  useLayoutEffect(() => {
    const ta = ref.current!
    if (!store.hasDraft(id) && ta.value !== content && !id.startsWith('ghost:')) {
      ta.value = content
      if (!fieldSizing) autosize(ta)
    }
  }, [content, id, store])

  useLayoutEffect(() => {
    const ta = ref.current!
    const req = store.caretReq
    if (req && req.id === id) {
      if (document.activeElement !== ta) ta.focus({ preventScroll: true })
      ta.setSelectionRange(req.start, req.end)
    }
  }, [caretSeq, id, store])

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    const ne = e.nativeEvent
    if (ne.isComposing || ne.keyCode === 229) return
    if (!handleEditKey(store, e.currentTarget, e)) store.noteKeyDown(e.currentTarget)
  }

  return (
    <textarea
      ref={ref}
      data-id={id}
      className="content editing"
      defaultValue={content}
      placeholder={placeholder}
      rows={1}
      spellCheck
      autoCapitalize="sentences"
      onKeyDown={onKeyDown}
      onBlur={() => {
        store.closePicker()
        store.commitDraft()
      }}
    />
  )
}
